import { useState, useEffect, useCallback, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

export interface ClientListItem {
  clientId: string;
  displayName: string;
  primaryContactName: string | null;
  primaryBrokerDisplay: string | null;
  officeId: string | null;
  officeName: string | null;
  email: string | null;
  phone: string | null;
  earliestSettlementDate: string | null;
  loanCount: number;
  hasActiveLoan: boolean;
  lastContactAt: unknown;
  status: string;
  ambiguousIdentity: boolean;
}

/** Stat-card counts over the FULL filtered set (not the current page). */
export interface ClientSummary {
  total: number;
  activeCount: number;
  withEmailCount: number;
  overdueCount: number;
}

/** Distinct filter options present in the caller's RBAC scope (pre-filter). */
export interface ClientFacets {
  offices: Array<{ id: string; name: string; count: number }>;
  brokers: Array<{ value: string; count: number }>;
  loanBooks: Array<{ entityCode: string; count: number }>;
}

/** Single-select filter selection shared by the table, cards, and Who-To-Call panel. */
export interface ClientCareFilters {
  officeId?: string | null;
  brokerDisplay?: string | null;
  loanBookEntityCode?: string | null;
  status?: 'active' | 'inactive' | null;
  overdueOnly?: boolean;
  /**
   * eDM client-tag filter (edm_content only): registry tag names sent as
   * listClients `tagNorms`. Absent or empty = no filter, and the key is then
   * omitted so the request is identical to the pre-tag one.
   */
  tagNorms?: string[] | null;
}

export const EMPTY_CLIENT_FILTERS: ClientCareFilters = {
  officeId: null,
  brokerDisplay: null,
  loanBookEntityCode: null,
  status: null,
  overdueOnly: false,
};

interface ListClientsResponse {
  clients: ClientListItem[];
  total: number;
  page: number;
  pageSize: number;
  summary: ClientSummary;
  facets: ClientFacets;
  hasAccess: boolean;
}

interface UseClientsParams {
  companyId?: string;
  page?: number;
  pageSize?: number;
  search?: string;
  filters?: ClientCareFilters;
}

const EMPTY_SUMMARY: ClientSummary = { total: 0, activeCount: 0, withEmailCount: 0, overdueCount: 0 };
const EMPTY_FACETS: ClientFacets = { offices: [], brokers: [], loanBooks: [] };

/** RBAC-scoped client roster via the listClients callable (broker→own, admin→office, super_admin→all). */
export function useClients({ companyId, page = 1, pageSize = 50, search, filters }: UseClientsParams) {
  const [clients, setClients] = useState<ClientListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<ClientSummary>(EMPTY_SUMMARY);
  const [facets, setFacets] = useState<ClientFacets>(EMPTY_FACETS);
  const [hasAccess, setHasAccess] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Stable primitive deps so the callback only re-fires on real filter changes.
  const officeId = filters?.officeId ?? null;
  const brokerDisplay = filters?.brokerDisplay ?? null;
  const loanBookEntityCode = filters?.loanBookEntityCode ?? null;
  const status = filters?.status ?? null;
  const overdueOnly = filters?.overdueOnly ?? false;
  // Joined to a primitive so a fresh array with the same tags does not refetch.
  const tagNormsKey = (filters?.tagNorms ?? []).join('\u0000');
  // Bumped per request: a reply for an older filter is dropped. The tagNorms
  // filter takes a slower server path, so out-of-order replies became likely.
  const reqSeq = useRef(0);

  const refetch = useCallback(async () => {
    if (!companyId) return;
    const mine = ++reqSeq.current;
    setLoading(true);
    setError(null);
    try {
      const fn = httpsCallable<Record<string, unknown>, ListClientsResponse>(functions, 'listClients');
      const res = await fn({
        companyId,
        page,
        pageSize,
        search: search?.trim() || undefined,
        officeId: officeId || undefined,
        brokerDisplay: brokerDisplay || undefined,
        loanBookEntityCode: loanBookEntityCode || undefined,
        status: status || undefined,
        overdueOnly: overdueOnly || undefined,
        ...(tagNormsKey ? { tagNorms: tagNormsKey.split('\u0000') } : {}),
      });
      if (mine !== reqSeq.current) return;
      setClients(res.data.clients ?? []);
      setTotal(res.data.total ?? 0);
      setSummary(res.data.summary ?? EMPTY_SUMMARY);
      setFacets(res.data.facets ?? EMPTY_FACETS);
      setHasAccess(res.data.hasAccess ?? true);
    } catch (e) {
      if (mine !== reqSeq.current) return;
      const msg = e instanceof Error ? e.message : 'Failed to load clients';
      setError(msg);
      console.error('[useClients] failed:', e);
    } finally {
      if (mine === reqSeq.current) setLoading(false);
    }
  }, [companyId, page, pageSize, search, officeId, brokerDisplay, loanBookEntityCode, status, overdueOnly, tagNormsKey]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { clients, total, summary, facets, hasAccess, loading, error, refetch };
}
